import { Controller } from 'stimulus'

const CSS = 'lexiqamus-page-toc'
const SECTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const TR_CHAR = { ı: 'i', ş: 's', ğ: 'g', ü: 'u', ö: 'o', ç: 'c' }
const MOBILE_MAX_WIDTH = 900

// Shared TOC for long LexiQamus static pages: builds nested list from flat source links,
// optional h3 subsections, scroll-spy. Mobile dropdown uses native <details> (_page_toc.html.erb).
export default class extends Controller {
  static targets = ['list', 'source', 'current', 'mobile']

  static values = {
    mode: String,
    article: String,
  }

  connect () {
    if (this.hasSourceTarget && !this.listTarget.children.length) this.populateTocFromSource()
    this.syncDetailsOpenState()
    this.stopScrollSpy = this.startScrollSpy()
  }

  disconnect () {
    this.stopScrollSpy?.()
  }

  closeMobileMenu () {
    if (this.hasMobileTarget && window.innerWidth <= MOBILE_MAX_WIDTH) {
      this.mobileTarget.removeAttribute('open')
    }
  }

  syncDetailsOpenState () {
    if (!this.hasMobileTarget) return
    if (window.innerWidth > MOBILE_MAX_WIDTH) this.mobileTarget.setAttribute('open', '')
    else this.mobileTarget.removeAttribute('open')
  }

  populateTocFromSource () {
    const article = this.contentArticle
    if (!article) return

    const mode = this.hasModeValue ? this.modeValue : 'decimal'
    const entries = []
    this.sourceTarget.content.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      const target = document.getElementById(decodeURIComponent(anchor.getAttribute('href').slice(1)))
      if (target) entries.push({ label: anchor.textContent.trim(), target, subsections: [] })
    })
    if (!entries.length) return

    if (mode !== 'plain') this.assignSubsectionsToEntries(article, entries)

    let letterIndex = 0
    entries.forEach((entry) => {
      if (mode === 'letters') {
        const heading = entry.target.tagName === 'H2' ? entry.target : entry.target.querySelector('h2')
        if (heading) {
          entry.sectionCode = SECTION_LETTERS[letterIndex++]
          this.addLetterBadgeToHeading(heading, entry.sectionCode)
          entry.subsections.forEach((subsection, index) => {
            subsection.label = subsection.element.textContent.trim()
            subsection.stepCode = `${entry.sectionCode}${index + 1}`
            subsection.element.setAttribute('data-code', subsection.stepCode)
          })
        }
      } else if (mode === 'decimal') {
        const parts = this.splitNumberPrefixFromHeading(entry.label)
        entry.sectionCode = parts[0]
        entry.label = parts[1]
        entry.subsections.forEach((subsection) => {
          const subParts = this.splitNumberPrefixFromHeading(subsection.element.textContent)
          subsection.stepCode = subParts[0]
          subsection.label = subParts[1]
        })
      }

      this.listTarget.appendChild(this.createSectionListItem(entry))
    })

    if (this.listTarget.querySelector('[data-code]')) this.element.classList.add(`${CSS}--with-codes`)
    this.element.setAttribute('data-mode', mode)
    this.element.classList.add(`${CSS}--built`)
  }

  assignSubsectionsToEntries (article, entries) {
    let entryIndex = 0
    article.querySelectorAll('h3').forEach((heading) => {
      if (heading.closest('aside, figure, .footnotes, [class*="footnote"]')) return
      while (entryIndex + 1 < entries.length &&
             entries[entryIndex + 1].target.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING) {
        entryIndex++
      }
      entries[entryIndex]?.subsections.push({ element: heading })
    })

    const pageLocale = document.documentElement.lang || 'en'
    entries.forEach((entry) => {
      if (entry.subsections.length === 1 &&
          entry.subsections[0].element.textContent.trim().toLocaleLowerCase(pageLocale) === entry.label.toLocaleLowerCase(pageLocale)) {
        entry.subsections[0].hideInList = true
      }
    })
  }

  createSectionListItem (entry) {
    const listItem = document.createElement('li')
    listItem.appendChild(this.createTocLink(entry.label, `#${entry.target.id}`, `${CSS}-section-link`, entry.sectionCode))

    const visibleSubsections = entry.subsections.filter((subsection) => !subsection.hideInList)
    if (visibleSubsections.length) {
      const subList = document.createElement('ol')
      visibleSubsections.forEach((subsection) => {
        subsection.id = subsection.element.id || (subsection.element.id = this.uniqueHeadingIdFromText(subsection.label))
        const stepItem = document.createElement('li')
        stepItem.appendChild(this.createTocLink(subsection.label, `#${subsection.id}`, null, subsection.stepCode))
        subList.appendChild(stepItem)
      })
      listItem.appendChild(subList)
    }

    return listItem
  }

  createTocLink (label, href, linkClass, code) {
    const link = document.createElement('a')
    link.href = href
    link.dataset.t = label
    link.dataset.action = 'click->lexiqamus-page-toc#closeMobileMenu'
    if (linkClass) link.className = linkClass
    if (code) {
      link.dataset.code = code
      const span = document.createElement('span')
      span.className = linkClass ? `${CSS}-section-code` : `${CSS}-step-code`
      span.textContent = code
      if (linkClass) span.setAttribute('aria-hidden', 'true')
      link.appendChild(span)
    }
    link.appendChild(document.createTextNode(label))
    return link
  }

  scrollSpyElementFor (element) {
    if (!element) return null
    if (element.matches('article, [class*="-page-article"]')) {
      return element.querySelector('h3') || element
    }
    if (element.matches('section')) {
      return element.querySelector('h2, h3') || element
    }
    return element
  }

  get contentArticle () {
    if (this.hasArticleValue) {
      return this.element.parentElement?.querySelector(this.articleValue) ||
        document.querySelector(this.articleValue)
    }
    return this.element.parentElement?.querySelector(':scope > main, :scope > [class*="page-article"], :scope > article')
  }

  startScrollSpy () {
    const activeClass = `${CSS}-link--active`
    const expandedClass = `${CSS}-section--expanded`
    const links = [...this.listTarget.querySelectorAll('a')]
    if (!links.length) return null

    const spyTargets = links.map((link) => {
      const id = decodeURIComponent(link.getAttribute('href').slice(1))
      return this.scrollSpyElementFor(document.getElementById(id))
    })
    let activeIndex = -1

    const scrollMarginTop = () => Math.min(window.innerHeight * 0.3, 240)

    const highlightActiveLink = (nextIndex) => {
      if (nextIndex === activeIndex) return
      activeIndex = nextIndex

      links.forEach((link, index) => link.classList.toggle(activeClass, index === nextIndex))
      const activeLink = links[nextIndex]
      const sectionListItem = activeLink.closest(`.${CSS}-list > li`)
      this.listTarget.querySelectorAll(':scope > li').forEach((listItem) => {
        listItem.classList.toggle(expandedClass, listItem === sectionListItem)
      })
      const sectionLink = sectionListItem?.querySelector(`.${CSS}-section-link`)
      if (this.hasCurrentTarget && sectionLink) this.updateMobileHeadingPreview(activeLink, sectionLink)

      if (window.innerWidth > MOBILE_MAX_WIDTH && this.element.scrollHeight > this.element.clientHeight + 2) {
        const linkRect = activeLink.getBoundingClientRect()
        const navRect = this.element.getBoundingClientRect()
        if (linkRect.top < navRect.top + 40 || linkRect.bottom > navRect.bottom - 20) {
          this.element.scrollTop += (linkRect.top - navRect.top) - this.element.clientHeight / 3
        }
      }
    }

    const findActiveLinkIndex = () => {
      const line = scrollMarginTop()
      let nextIndex = 0
      spyTargets.forEach((target, index) => {
        if (target && target.getBoundingClientRect().top <= line) nextIndex = index
      })
      return nextIndex
    }

    const onScroll = () => highlightActiveLink(findActiveLinkIndex())

    const onResize = () => {
      activeIndex = -1
      this.syncDetailsOpenState()
      highlightActiveLink(findActiveLinkIndex())
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    highlightActiveLink(findActiveLinkIndex())

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }

  updateMobileHeadingPreview (activeLink, sectionLink) {
    this.currentTarget.replaceChildren()

    if (activeLink.dataset.code) {
      const codeBadge = document.createElement('span')
      codeBadge.className = `${CSS}-current-code`
      codeBadge.textContent = activeLink.dataset.code
      this.currentTarget.appendChild(codeBadge)
    }

    const sectionTitle = document.createElement('b')
    if (activeLink !== sectionLink) sectionTitle.className = `${CSS}-current-section-title`
    sectionTitle.textContent = sectionLink.dataset.t
    this.currentTarget.appendChild(sectionTitle)

    if (activeLink !== sectionLink) {
      const stepTitle = document.createElement('span')
      stepTitle.className = `${CSS}-current-step-title`
      stepTitle.textContent = activeLink.dataset.t
      this.currentTarget.appendChild(stepTitle)
    }
  }

  addLetterBadgeToHeading (heading, code) {
    if (heading.querySelector('.sec-letter, .lexiqamus-user-guide-page-section-badge')) return

    heading.classList.add('has-letter')

    const title = document.createElement('span')
    title.className = 'sec-title'
    while (heading.firstChild) title.appendChild(heading.firstChild)

    const badge = document.createElement('span')
    badge.className = 'sec-letter'
    badge.setAttribute('aria-hidden', 'true')
    badge.textContent = code

    heading.append(badge, title)
  }

  splitNumberPrefixFromHeading (text) {
    const match = text.match(/^\s*(\d+(?:\.\d+)*)\.?\s+(.+)$/)
    return match ? [match[1], match[2]] : [null, text.trim()]
  }

  uniqueHeadingIdFromText (text) {
    let slug = text.toLowerCase()
      .replace(/[ışğüöç]/g, (c) => TR_CHAR[c])
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section'

    let uniqueId = slug
    let suffix = 2
    while (document.getElementById(uniqueId)) {
      uniqueId = `${slug}-${suffix++}`
    }
    return uniqueId
  }
}
